import React, { useMemo } from 'react';
import { Clock, FileText, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import Badge from '@components/common/Badge';
import { Assessment, AssessmentItem } from '@app-types';
import { addDaysToDate, formatCalendarDate, toLocalDateInput, utcToLocal } from '@utils/dateUtils';
import styles from './EvidenceRequirementsSummary.module.css';

const EVIDENCE_DUE_FALLBACK_DAYS = 30;

/** First 10 chars (YYYY-MM-DD) of a calendar-day value, or null when it isn't a valid day */
const toCalendarDay = (value: string): string | null => {
  const day = value.slice(0, 10);
  return Number.isNaN(Date.parse(`${day}T00:00:00.000Z`)) ? null : day;
};

/**
 * The evidence due date as a calendar day (YYYY-MM-DD), or null when it can't be known
 *
 * The due date is a `@db.Date` column, so it stays a plain calendar day end to end: no
 * timezone conversion can shift it. The 30-day fallback is added on that calendar day
 * in UTC, because `Date#setDate` works in the viewer's zone and drifts across DST changes.
 */
const resolveEvidenceDueDay = (
  assessment: Pick<Assessment, 'evidence_due_date' | 'completed_date' | 'scheduled_date'>
): string | null => {
  if (assessment.evidence_due_date) {
    return toCalendarDay(assessment.evidence_due_date);
  }
  // Fallback: 30 days after completed_date (a calendar day) or, failing that,
  // scheduled_date (a meeting instant, so its day is the viewer's local day).
  if (assessment.completed_date) {
    const completedDay = toCalendarDay(assessment.completed_date);
    return completedDay ? addDaysToDate(completedDay, EVIDENCE_DUE_FALLBACK_DAYS) : null;
  }
  if (assessment.scheduled_date) {
    const scheduled = utcToLocal(assessment.scheduled_date);
    return Number.isNaN(scheduled.getTime())
      ? null
      : addDaysToDate(toLocalDateInput(scheduled), EVIDENCE_DUE_FALLBACK_DAYS);
  }
  return null;
};

interface EvidenceRequirementsSummaryProps {
  assessmentItems: AssessmentItem[];
  assessment: Assessment;
}

const EvidenceRequirementsSummary: React.FC<EvidenceRequirementsSummaryProps> = ({ assessmentItems, assessment }) => {
  // Filter processes that require evidence
  const evidenceRequired = useMemo(() => {
    return assessmentItems.filter((item) => item.evaluation === 'Implemented' && item.requires_evidence);
  }, [assessmentItems]);

  const { evidence_due_date, completed_date, scheduled_date } = assessment;
  const evidenceDueDay = useMemo(
    () => resolveEvidenceDueDay({ evidence_due_date, completed_date, scheduled_date }),
    [evidence_due_date, completed_date, scheduled_date]
  );

  // Calculate days remaining/overdue
  const dueDateInfo = useMemo(() => {
    if (!evidenceDueDay) return null;

    const now = new Date();
    const diffTime = new Date(`${evidenceDueDay}T00:00:00.000Z`).getTime() - now.getTime();
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let urgency: 'critical' | 'warning' | 'info';
    let message: string;

    if (daysRemaining < 0) {
      urgency = 'critical';
      message = `${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? 's' : ''} overdue`;
    } else if (daysRemaining < 3) {
      urgency = 'critical';
      message = `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`;
    } else if (daysRemaining <= 7) {
      urgency = 'warning';
      message = `${daysRemaining} days remaining`;
    } else {
      urgency = 'info';
      message = `${daysRemaining} days remaining`;
    }

    return { urgency, message, daysRemaining };
  }, [evidenceDueDay]);

  // Don't show if no evidence required (check after all hooks)
  if (evidenceRequired.length === 0) {
    return null;
  }

  return (
    <div className={styles.evidenceRequirementsCard}>
      <div className={styles.evidenceRequirementsHeader}>
        <FileText size={24} />
        <h3>Evidence Required</h3>
      </div>

      <div className={styles.evidenceRequirementsSummary}>
        <AlertCircle size={20} className={styles.summaryIcon} />
        <p className={styles.summaryText}>
          <strong>{evidenceRequired.length}</strong> process{evidenceRequired.length !== 1 ? 'es' : ''} require evidence
          submission from the assigned Contract Owner or Contract Manager
        </p>
      </div>

      {evidenceDueDay && dueDateInfo && (
        <div
          className={clsx(
            styles.evidenceDueDate,
            styles[`evidenceDueDate${dueDateInfo.urgency.charAt(0).toUpperCase() + dueDateInfo.urgency.slice(1)}`]
          )}
        >
          <div className={styles.dueDateMain}>
            <Clock size={18} />
            <div className={styles.dueDateDetails}>
              <span className={styles.dueDateLabel}>Evidence Due:</span>
              <span className={styles.dueDateValue}>{formatCalendarDate(evidenceDueDay)}</span>
            </div>
          </div>
          <div
            className={clsx(
              styles.dueDateCountdown,
              styles[`dueDateCountdown${dueDateInfo.urgency.charAt(0).toUpperCase() + dueDateInfo.urgency.slice(1)}`]
            )}
          >
            {dueDateInfo.message}
          </div>
        </div>
      )}

      <div className={styles.evidenceProcessList}>
        <h4>Processes Requiring Evidence:</h4>
        <ul>
          {evidenceRequired.map((item) => (
            <li key={item.id} className={styles.evidenceProcessItem}>
              <div className={styles.processInfo}>
                <span className={styles.processName}>{item.process_name}</span>
                <span className={styles.processDomain}>{item.domain_name}</span>
              </div>
              <Badge variant="warning">Evidence Required</Badge>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.evidenceRequirementsNote}>
        <p>
          <strong>Note:</strong> Assigned Contract Owner(s) and Contract Manager(s) have been notified and will upload evidence for these processes.
          You will be notified when evidence is submitted for review.
        </p>
      </div>
    </div>
  );
};

export default EvidenceRequirementsSummary;
